import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Injectable,
} from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import { accountIdQueryParam } from '@rivethealth/data/query-params';
import { catchError, of } from 'rxjs';
import config from '../../admin/config';
import { PasswordService } from '../../api/password.service';
import { AuthService } from '../../common/auth/auth.service';
import { ENVIRONMENT } from '../../common/core/environment';
import { HookDirective } from '../../common/core/hook';
import { useEffect, useState, useTriggerLoad } from '../../common/core/hooks';
import { emailIsValid } from '../../common/input/email';
import { StringParam } from '../../common/state/route';
import { RouteService, useQueryParam } from '../../common/state/route.service';
import { accountAuthPersistence } from '../account/auth';

@Injectable({ providedIn: 'root' })
export class LoginGuard implements CanActivate {
  constructor(
    @Inject(ENVIRONMENT) private readonly environment: string,
    private readonly auth: AuthService,
    private readonly router: Router,
  ) {}
  canActivate(route: ActivatedRouteSnapshot): boolean {
    if (
      route.queryParamMap.has(accountIdQueryParam) &&
      route.queryParamMap.has('token')
    ) {
      // A super admin has arrived from Riveter or a user has
      // logged in using saml
      this.auth.authenticate(
        route.queryParamMap.get('token')!,
        accountAuthPersistence(
          +route.queryParamMap.get(accountIdQueryParam)!,
          this.environment,
        ),
      );
      this.router.navigateByUrl(
        route.queryParamMap.get('next') ||
          `/accounts/${route.queryParamMap.get(accountIdQueryParam)}`,
        {
          replaceUrl: true,
        },
      );
      return false;
    }
    if (this.auth.isAuthenticated()) {
      // The logged-in user has navigated directly to /login.
      this.router.navigateByUrl(route.queryParamMap.get('next') || '/');
      return false;
    }
    return true;
  }
}

@Injectable({ providedIn: 'root' })
export class LogoutGuard implements CanActivate {
  constructor(private readonly auth: AuthService) {}
  canActivate(route: ActivatedRouteSnapshot): boolean {
    this.auth.deauthenticate();
    return true;
  }
}

@Component({
  host: { class: 'rv-grid xlarge outer' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-login',
  styleUrls: ['./login.component.scss'],
  templateUrl: './login.component.html',
})
export class LoginComponent extends HookDirective {
  constructor(
    @Inject(ENVIRONMENT) private readonly environment: string,
    private readonly auth: AuthService,
    private readonly routeService: RouteService,
    private readonly router: Router,
    private readonly passwordService: PasswordService,
    _cdr: ChangeDetectorRef,
  ) {
    super(_cdr);
  }

  get _() {
    const [next] = useQueryParam(this.routeService, new StringParam('next'));

    const [envIsZelis, setEnvIsZelis] = useState<boolean>(!!config.envIsZelis);

    const [emailQueryParam] = useQueryParam(
      this.routeService,
      new StringParam('email'),
    );
    const [accountId, setAccountId] = useState(0);
    const [accounts, setAccounts] = useState<{ id: number; name: string }[]>(
      [],
    );
    const [token, setToken] = useState('');
    const [email, setEmail] = useState(emailQueryParam ?? '');
    const [emailHasIssue, setEmailHasIssue] = useState(false);
    const [password, setPassword] = useState('');
    const [passwordHasIssue, setPasswordHasIssue] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [clientSideError, setClientSideError] = useState('');

    function validateAndSubmit() {
      clearAllErrors();
      if (email.length === 0 && password.length === 0) {
        setEmailHasIssue(true);
        setPasswordHasIssue(true);
        setClientSideError('Please enter email and password.');
      } else if (email.length === 0) {
        setEmailHasIssue(true);
        setClientSideError('Please enter email address.');
      } else if (!emailIsValid(email)) {
        setEmailHasIssue(true);
        setClientSideError(
          'This does not appear to be a valid email address. Please make sure it is correct and try again.',
        );
      } else if (password.length === 0) {
        setPasswordHasIssue(true);
        setClientSideError('Please enter password.');
      } else {
        doSubmit();
      }
    }
    function clearAllErrors() {
      setEmailHasIssue(false);
      setPasswordHasIssue(false);
      setClientSideError('');
      submitLoad.error = null;
    }

    const [submitLoad, doSubmit] = useTriggerLoad(() => {
      this.auth.deauthenticate();
      return this.passwordService.generateAuthentication(email, password).pipe(
        catchError(e => {
          if (e.error?.message == 'User must reset password') {
            this.auth.deauthenticate();
            this.router.navigate(['login', 'forgot'], {
              queryParams: { reason: 'forced' },
            });
            return of({ accounts: [], token: null });
          } else {
            throw e;
          }
        }),
      );
    });

    useEffect(() => {
      if (!submitLoad.loading && !submitLoad.erred && submitLoad.data) {
        let { accounts, token } = submitLoad.data;
        if (token) {
          accounts = accounts.filter(a => a.authentication == 'password');
          if (accounts?.length == 1) {
            setAccountId(accounts[0].id);
          } else {
            setAccounts(accounts);
          }
          setToken(token);
        }
      }
    }, [submitLoad]);

    useEffect(() => {
      if (accountId && token) {
        this.auth.authenticate(
          token,
          accountAuthPersistence(accountId!, this.environment),
        );
        this.router.navigateByUrl(next || `/accounts/${accountId}`);
      }
    }, [accountId]);

    return {
      envIsZelis,
      setEnvIsZelis,
      next,
      emailQueryParam,
      email,
      setEmail,
      password,
      showPassword,
      setShowPassword,
      accounts,
      setAccountId,
      setPassword,
      emailHasIssue,
      passwordHasIssue,
      clientSideError,
      validateAndSubmit,
      clearAllErrors,
      submitLoad,
    };
  }

  openIntercom() {
    Intercom('show');
  }
}
